import React, {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {USER_TABS} from 'src/constants';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {selectUserDetails} from 'src/modules/profile/user/services/user-page-slice';
import {selectUserInformation} from 'src/modules/profile/user/services/user-profile-slice';

import {Tab, Tabs} from '@pb/kit-component-tabs-v2';


import {userPageTabs} from './user-page-tabs.module.styl';

type Props = {
    userId: number,
    activeTab: string,
};

export const UserPageTabs: React.FC<Props> = ({userId, activeTab}: Props) => {
    const {canManageUserRole, isAdminAccount, hasAccess, canViewUserProducts} = useAdminAccessRight();
    const navigate = useNavigate();
    const userDetails = useSelector(selectUserInformation);
    const {isAccountManager: showAssignedAccounts} = useSelector(selectUserDetails);
    const {userTabChange} = useEventTracking();
    const showEntitlements = hasAccess(ACCESS_RIGHTS.USER_ENTITLEMENTS_VIEW);

    const onTabClick = useCallback((tab: string) => {
        userTabChange({activeTab: tab, previousTab: activeTab});
        navigate(`/profile/user/${userId}/${tab}`);
    }, [userId, activeTab, navigate, userTabChange]);

    const shownUserRoleSettings = useMemo(() => (
        !!userDetails?.account?.accountId && canManageUserRole(userDetails?.account?.accountId)
    ), [userDetails, canManageUserRole]);

    const showConferences = useMemo(() => (
        (userDetails?.virtualAccounts?.length ?? 0) > 0
    ), [userDetails]);

    const showActivityLog = useMemo(() => (
        !!userDetails?.account?.accountId && isAdminAccount(userDetails?.account?.accountId)
    ), [isAdminAccount, userDetails]);

    const showUserLimits = useMemo(() => hasAccess(ACCESS_RIGHTS.USER_LIMITS_VIEW), [hasAccess]);
    const showContentTab = useMemo(() => hasAccess(ACCESS_RIGHTS.USER_CONTENT_VIEW), [hasAccess]);
    const showProductTab = useMemo(() =>
        canViewUserProducts(userDetails?.account?.accountId || 0), [canViewUserProducts, userDetails]);

    return (
        <Tabs onChange={onTabClick} value={activeTab} className={userPageTabs} style={{width: '100%'}}>
            {Object.values(USER_TABS)
                .filter(({name}) => name !== USER_TABS.ASSIGN_ENTITLEMENTS.name)
                .filter(({name}) => name !== USER_TABS.ROLE_SETTINGS.name || shownUserRoleSettings)
                .filter(({name}) => name !== USER_TABS.CONFERENCES.name || showConferences)
                .filter(({name}) => name !== USER_TABS.ASSIGNED_ACCOUNTS.name || showAssignedAccounts)
                .filter(({name}) => name !== USER_TABS.ACTIVITY_LOG.name || showActivityLog)
                .filter(({name}) => name !== USER_TABS.ENTITLEMENTS.name || showEntitlements)
                .filter(({name}) => name !== USER_TABS.LIMITS.name || showUserLimits)
                .filter(({name}) => name !== USER_TABS.CONTENT.name || showContentTab)
                .filter(({name}) => name !== USER_TABS.PRODUCTS.name || showProductTab)
                .map(({caption, name}) => (
                    <Tab value={name} key={name} {...{caption, name}}/>
                ))}
        </Tabs>
    );
};
